// Presentation-only title casing for optional-task titles (SAD-338). Storage
// stays exactly as extracted — deriveOptionalTaskTitle (pangaea) deliberately
// never touches letter case, since contract text is often ALL CAPS and
// case-folding risks mangling acronyms. Matt's ask was consistency: OMEGA's
// 24 real titles mix ALL CAPS and Title Case verbatim in the same table.
//
// The decision is applied per-word rather than by gating on a whole-title
// "is this shouting" ratio: several real OMEGA titles are a short ALL CAPS
// task name followed by a long, already-mixed-case trailing clause SAD-118
// owns ("… IAW PWS Paragraph 5.4.7, including all sub-paragraphs.Total…") —
// that tail's length dominates any whole-string uppercase/lowercase ratio and
// would wrongly suppress normalization of the genuinely-shouting lead words
// (verified against all 24 real titles; a char-ratio gate misclassified 6 of
// them). Per-word decisions don't have that failure mode: a word already
// carrying a lowercase letter (the entire trailing clause, which is already
// correctly cased) is left alone regardless of its neighbors, and a title
// that's already all mixed-case prose (e.g. "Periodic Data Collection and
// Data Updates") never has a normalizable word in the first place, so it
// comes out byte-for-byte unchanged with no separate gate required.
// General government-contracting acronyms — deliberately NOT tied to any one
// contract (OMEGA's specific strings would be brittle against unseen text).
// LCNC/IT/PWS/CLIN/SLIN are named in SAD-338 itself; SAAS/IAW/API were
// observed in OMEGA's live optional-task titles (preflight §4); the rest are
// standard govcon/contracting vocabulary likely to recur on other contracts.
export const GOVCON_TITLE_ACRONYMS = new Set([
    'IT',
    'PWS',
    'CLIN',
    'SLIN',
    'LCNC',
    'SAAS',
    'IAW',
    'API',
    'APIS',
    'SOW',
    'POP',
    'FFP',
    'IDIQ',
    'CDRL',
    'FAR',
    'DFARS',
    'RFP',
    'RFQ',
    'RFI',
    'KO',
    'COR',
    'CPARS',
    'QASP'
]);
function titleCaseWord(word) {
    if (!word) return word;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}
// One non-whitespace token. Preserved verbatim (never re-cased) when it:
//   - carries a digit ("5.4.4", "5.4.8Total") — identifier-shaped, not prose
//   - contains "&" (the "&" itself needs no casing, and pairs like "T&M" are
//     identifiers, not words)
//   - already has a lowercase letter — it's already-cased prose (e.g. the
//     "Paragraph"/"Total Iteration Price" trailing text SAD-118 owns, or the
//     lowercase "s" that already saves "(APIs)")
//   - is a known govcon acronym once leading/trailing punctuation is stripped
//     (e.g. "-SAAS", "(LCNC)")
// A slash-joined compound ("ACCELERATION/DELIVERY/MAINTENANCE") reads as
// multiple words, not one identifier — split it and normalize each side.
function normalizeToken(token) {
    if (!token) return token;
    if (/[0-9]/.test(token) || token.includes('&') || /[a-z]/.test(token)) return token;
    if (token.includes('/')) return token.split('/').map(normalizeToken).join('/');
    const match = token.match(/^(\W*)([\s\S]*?)(\W*)$/);
    if (!match) return titleCaseWord(token);
    const [, lead, core, trail] = match;
    if (!core) return token;
    if (GOVCON_TITLE_ACRONYMS.has(core.toUpperCase())) return `${lead}${core}${trail}`;
    return `${lead}${titleCaseWord(core)}${trail}`;
}
// Pure — never mutates `title`; returns `title` itself, unchanged, when
// nothing in it needs normalizing.
export function presentOptionalTaskTitle(title) {
    if (!title) return title;
    return title.split(/(\s+)/).map((segment)=>/^\s+$/.test(segment) ? segment : normalizeToken(segment)).join('');
}
